import { useEffect, useRef, useState, type ReactNode } from "react";
import {
  BLACKJACK_RULES_TEXT,
  CASINO_DRINKS,
  CASINO_RULES,
  MONEY_WHEEL_LABELS,
  MONEY_WHEEL_PAYS,
  MONEY_WHEEL_SYMBOLS,
  PLAY_CHIPS_NOTICE,
  SLOT_PAYTABLE,
  SLOT_SYMBOL_LABELS,
  slotSymbolsAt,
  type CasinoDrink,
  type MoneyWheelSymbol,
  type SlotMachineState,
} from "@townsy/contracts";
import { SignUpToPlay } from "../SignUpToPlay";
import type { VenueHudProps } from "../types";
import { SlotView } from "./SlotView";
import { TableView } from "./TableView";
import { asCasinoPrivate, asCasinoState, chipsText, secondsLeft } from "./casinoView";
import { ChipPicker, Cards, OUTCOME_TEXT, SeatControls, handTotal, offTitle, phaseText, type Ctx } from "./hudParts";
import "./casino.css";

/**
 * Townsy Casino overlay (ADR-0016). The controls follow where the visitor stands (the server says it in the private
 * state): a blackjack table, a slot machine, the money wheel's counter or the bar; elsewhere a short guide. Seated at a
 * table, the big table view opens; at a free (or your) slot machine, the big machine view opens; Minimize returns to
 * the compact panel and "Show table" / "Open machine" brings them back. Every button only sends an intent; the cards,
 * reels, wheel and chips shown are the server's. Play chips have no value and the notice says so at all times.
 * A guest (ADR-0048: betting needs an account) watches the same tables, reels and wheel and uses the bar; where the
 * betting controls would be, "Sign up (18+) to play" links to the sign-up, and the big machine view does not open.
 */
const WHEEL_STAKES = [1, 5, 10, 25] as const;
/** How long your last result stays up, and a room notice (a drink ordered, a big win). */
const RESULT_SHOWN_MS = 12_000;
const NOTICE_SHOWN_MS = 8_000;
/** Standing this long at a free machine opens its big view (walking past the row does not). */
export const SLOT_OPEN_AFTER_MS = 800;

export function CasinoHud(props: VenueHudProps) {
  const state = asCasinoState(props.shared);
  const mine = asCasinoPrivate(props.mine);
  const now = useNow(500);
  const received = useRef<{ shared: unknown; at: number }>({ shared: null, at: 0 });
  if (received.current.shared !== props.shared) received.current = { shared: props.shared, at: Date.now() };
  const receivedAt = received.current.at;
  const receivedMine = useRef<{ mine: unknown; at: number }>({ mine: null, at: 0 });
  if (receivedMine.current.mine !== props.mine) receivedMine.current = { mine: props.mine, at: Date.now() };
  const mineAt = receivedMine.current.at;
  // Since when the visitor has stood at the same thing (big views open after a moment, not while walking past).
  const nearKey = mine?.near ? JSON.stringify(mine.near) : null;
  const nearSince = useRef<{ key: string | null; at: number }>({ key: null, at: 0 });
  if (nearSince.current.key !== nearKey) nearSince.current = { key: nearKey, at: Date.now() };
  // The big view the visitor minimized (it stays minimized until they sit or step up somewhere else), and a machine
  // they asked to open right away.
  const [minimized, setMinimized] = useState<string | null>(null);
  const [openNow, setOpenNow] = useState<string | null>(null);

  const chips = props.chips ?? 0;
  const betting = props.chanceGamesEnabled && (state?.bettingOpen ?? true);
  const signUp = props.guest === true && betting;
  // Your own results (the server sends them to you only) and, below, the room's notices and big wins.
  const result = [...props.events].reverse().find((e) => e.type === "result" && now - e.at < RESULT_SHOWN_MS && e.message);
  const notice = [...props.events].reverse().find((e) => (e.type === "notice" || e.type === "win") && now - e.at < NOTICE_SHOWN_MS && e.message);

  // Which big view applies: your seat, else the machine you stand at (yours, or free after a moment).
  let bigKey: string | null = null;
  if (state && mine) {
    if (mine.seat) bigKey = `table:${mine.seat.table}:${mine.seat.seat}`;
    else if (mine.near?.kind === "slot") {
      const m = state.slots[mine.near.machine];
      const held = mine.machine === mine.near.machine;
      const free = m !== undefined && m.actorId === null;
      // The big machine view is for playing: a guest watches from the compact panel.
      if (held || (!signUp && free && (openNow === nearKey || now - nearSince.current.at >= SLOT_OPEN_AFTER_MS))) bigKey = `slot:${mine.near.machine}`;
    }
  }
  const lastBig = useRef<string | null>(null);
  useEffect(() => {
    // Leaving the seat or the machine forgets the minimize, so the next time it opens again.
    if (lastBig.current !== bigKey && bigKey === null) setMinimized(null);
    lastBig.current = bigKey;
  }, [bigKey]);
  const bigOpen = bigKey !== null && minimized !== bigKey;

  let body: ReactNode;
  if (!state || !mine) {
    body = <p className="casino-guide">Getting the tables ready…</p>;
  } else {
    const ctx: Ctx = {
      state,
      mine,
      chips,
      betting,
      signUp,
      send: props.send,
      left: (endsIn) => secondsLeft(endsIn, receivedAt, now),
      leftMine: (ms) => secondsLeft(ms, mineAt, now) ?? 0,
      localActorId: props.localActorId,
      now,
    };
    const near = mine.near;
    const reopen = bigKey && !bigOpen ? () => setMinimized(null) : null;
    if (bigOpen && mine.seat) body = <TableView ctx={ctx} table={mine.seat.table} onMinimize={() => setMinimized(bigKey)} />;
    else if (bigOpen && near?.kind === "slot") body = <SlotView ctx={ctx} machine={near.machine} onMinimize={() => setMinimized(bigKey)} />;
    else if (near?.kind === "table") body = <TablePanel ctx={ctx} table={near.table} onShow={reopen} />;
    else if (near?.kind === "slot") body = <SlotPanel ctx={ctx} machine={near.machine} onShow={reopen ?? (bigKey === null && !signUp ? () => setOpenNow(nearKey) : null)} />;
    else if (near?.kind === "wheel") body = <WheelPanel ctx={ctx} />;
    else if (near?.kind === "bar") body = <BarPanel ctx={ctx} />;
    else body = <Guide signUp={signUp} />;
  }

  return (
    <div className={`venue-hud-body casino-hud${bigOpen ? " casino-hud-big" : ""}`} data-testid="casino-hud">
      {body}
      {result && !bigOpen ? (
        <p className="casino-result" role="status" aria-live="polite" data-testid="casino-result">
          {result.message}
        </p>
      ) : null}
      {notice ? (
        <p className={`small ${notice.type === "win" ? "casino-win" : "casino-muted"}`} data-testid="casino-notice">
          {notice.message}
        </p>
      ) : null}
      {props.chips !== null && props.chips <= 0 ? <p className="small">Out of play chips? Walk out and back in to get 100.</p> : null}
      <p className="casino-notice" data-testid="casino-chips-notice">
        {PLAY_CHIPS_NOTICE}
      </p>
    </div>
  );
}

function useNow(everyMs: number): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = window.setInterval(() => setNow(Date.now()), everyMs);
    return () => window.clearInterval(id);
  }, [everyMs]);
  return now;
}

function Guide({ signUp }: { signUp: boolean }) {
  return (
    <div className="casino-panel" data-testid="casino-guide">
      <p className="casino-guide">
        {signUp
          ? "Blackjack, the slot machines and the money wheel are for signed-up players. Walk up to any of them to watch. "
          : "Walk up to a blackjack table, a slot machine or the money wheel to play. "}
        The bar is in the north-east corner: grab a free mocktail and chat with whoever is around.
      </p>
      {signUp ? <SignUpToPlay watch="Guests can watch the games, use the bar and chat." /> : null}
    </div>
  );
}

// ---------------------------------------------------------------------------------------------------------------------
// Blackjack (compact)
// ---------------------------------------------------------------------------------------------------------------------

function TablePanel({ ctx, table, onShow }: { ctx: Ctx; table: number; onShow: (() => void) | null }) {
  const t = ctx.state.tables[table];
  const [amount, setAmount] = useState<number>(5);
  if (!t) return null;
  const seat = ctx.mine.seat?.table === table ? ctx.mine.seat.seat : null;
  const me = seat !== null ? (t.seats[seat] ?? null) : null;
  const left = ctx.left(t.endsIn);
  const full = t.seats.every((s) => s !== null);
  return (
    <div className="casino-panel" data-testid="casino-table" data-table={table}>
      <div className="casino-title">
        <h3>Blackjack · table {table + 1}</h3>
        <span className="casino-phase" data-testid="casino-table-phase">
          {phaseText(t, left, seat)}
        </span>
        {onShow ? (
          <button type="button" className="btn btn-sm casino-show" onClick={onShow} data-testid="casino-show-table">
            Show table
          </button>
        ) : null}
      </div>
      <div className="casino-hand">
        <span className="casino-hand-label">Dealer</span>
        <Cards cards={t.dealer} label="Dealer's cards" />
        {t.dealer.length ? <span className="casino-total">{t.dealer.includes("XX") ? `${t.dealerTotal} + ?` : t.dealerTotal}</span> : null}
      </div>
      {me ? (
        <div className="casino-hand mine" data-testid="casino-my-hand">
          <span className="casino-hand-label">You · seat {(seat ?? 0) + 1}</span>
          <Cards cards={me.cards} label="Your cards" />
          {me.cards.length ? <span className="casino-total">{handTotal(me)}</span> : null}
          {me.bet > 0 ? <span className="casino-bet">bet {me.bet}</span> : null}
          {me.outcome ? <span className={`casino-outcome ${me.outcome}`}>{OUTCOME_TEXT[me.outcome]}{me.payout ? ` +${me.payout}` : ""}</span> : null}
        </div>
      ) : null}
      <ul className="casino-seats" aria-label="Seats">
        {t.seats.map((s, i) =>
          i === seat ? null : (
            <li key={i}>
              <span className="casino-seat-no">Seat {i + 1}</span>{" "}
              {s ? (
                <>
                  {s.label}
                  {s.bet ? ` · bet ${s.bet}` : ""}
                  {s.cards.length ? ` · ${handTotal(s)}` : ""}
                  {s.outcome ? ` · ${OUTCOME_TEXT[s.outcome]}` : ""}
                </>
              ) : (
                <span className="casino-muted">free</span>
              )}
            </li>
          ),
        )}
      </ul>
      {me === null && ctx.signUp ? (
        <SignUpToPlay watch="Guests can watch the table." />
      ) : me === null ? (
        <div className="casino-actions">
          <button
            type="button"
            className="btn btn-primary btn-sm"
            disabled={!ctx.betting || full}
            title={offTitle(ctx.betting, full ? "Every seat is taken." : undefined)}
            onClick={() => ctx.send({ type: "bj.sit", table })}
            data-testid="casino-sit"
          >
            Sit down
          </button>
          {full ? <span className="small casino-muted">Every seat is taken.</span> : null}
        </div>
      ) : (
        <SeatControls ctx={ctx} table={table} amount={amount} setAmount={setAmount} />
      )}
      <details className="casino-rules">
        <summary>Rules: blackjack pays 3 to 2 · no split, no insurance</summary>
        {BLACKJACK_RULES_TEXT}
      </details>
    </div>
  );
}

// ---------------------------------------------------------------------------------------------------------------------
// Slots (compact)
// ---------------------------------------------------------------------------------------------------------------------

function SlotPanel({ ctx, machine, onShow }: { ctx: Ctx; machine: number; onShow: (() => void) | null }) {
  const m: SlotMachineState | undefined = ctx.state.slots[machine];
  const [bet, setBet] = useState<1 | 5 | 10>(1);
  if (!m) return null;
  const mineHeld = ctx.mine.machine === machine;
  const busy = m.actorId !== null && !mineHeld;
  const symbols = slotSymbolsAt(m.stops).map((s) => SLOT_SYMBOL_LABELS[s]);
  const status = busy ? `${m.label ?? "Someone"} is playing` : mineHeld ? "You are playing" : "Free: spin to play";
  return (
    <div className="casino-panel" data-testid="casino-slot" data-machine={machine}>
      <div className="casino-title">
        <h3>Slot machine {machine + 1}</h3>
        <span className="casino-phase">{status}</span>
        {onShow && !busy ? (
          <button type="button" className="btn btn-sm casino-show" onClick={onShow} data-testid="casino-show-machine">
            Open machine
          </button>
        ) : null}
      </div>
      <p className="casino-reels" aria-live="polite" data-testid="casino-reels">
        {m.spin === 0 ? "Pull up and spin." : m.spinning ? "Spinning…" : `${symbols.join(" | ")}${m.win > 0 ? ` · paid ${m.win}` : " · no win"}`}
      </p>
      {ctx.signUp ? (
        <SignUpToPlay watch="Guests can watch the reels." />
      ) : (
        <div className="casino-actions">
          <ChipPicker label="Bet" values={CASINO_RULES.slots.bets} value={bet} max={ctx.chips} onPick={(v) => setBet(v)} disabled={!ctx.betting} />
          <button
            type="button"
            className="btn btn-primary btn-sm"
            disabled={!ctx.betting || busy || m.spinning || ctx.chips < bet}
            title={offTitle(ctx.betting, busy ? "Someone else is playing this machine." : ctx.chips < bet ? `You have ${chipsText(ctx.chips)}.` : undefined)}
            onClick={() => ctx.send({ type: "slot.spin", machine, bet })}
            data-testid="casino-spin"
          >
            Spin · {bet}
          </button>
        </div>
      )}
      <div className="casino-paytable">
        <span className="casino-picker-label">Pays (times your bet):</span>
        <ul aria-label="Paytable, times your bet">
          {SLOT_PAYTABLE.map((line) => (
            <li key={line.label}>
              {line.label} <b>×{line.pays}</b>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------------------------------------------------
// Money wheel
// ---------------------------------------------------------------------------------------------------------------------

function WheelPanel({ ctx }: { ctx: Ctx }) {
  const w = ctx.state.wheel;
  const [symbol, setSymbol] = useState<MoneyWheelSymbol>("1");
  const [stake, setStake] = useState<number>(5);
  const left = ctx.left(w.endsIn);
  const myBets = ctx.mine.wheelRound === w.round ? ctx.mine.wheelBets : {};
  const staked = Object.values(myBets).reduce((a, b) => a + (b ?? 0), 0);
  const room = CASINO_RULES.wheel.maxPerRound - staked;
  const open = w.phase === "betting";
  const phase = open ? `Bets close in ${left ?? 0} s` : w.phase === "spinning" ? "The wheel is spinning…" : `Stopped on ${w.result ? MONEY_WHEEL_LABELS[w.result] : "?"}`;
  const can = ctx.betting && open && stake <= room && stake <= ctx.chips;
  const canClear = open && staked > 0;
  return (
    <div className="casino-panel" data-testid="casino-wheel">
      <div className="casino-title">
        <h3>Money wheel</h3>
        <span className="casino-phase" data-testid="casino-wheel-phase">
          {phase}
        </span>
      </div>
      <span className="casino-picker" role="group" aria-label="Pick a symbol">
        {MONEY_WHEEL_SYMBOLS.map((s) => (
          <button
            key={s}
            type="button"
            className={`btn btn-sm casino-symbol sym-${s}`}
            aria-pressed={symbol === s}
            disabled={!ctx.betting || ctx.signUp}
            onClick={() => setSymbol(s)}
            data-testid={`casino-symbol-${s}`}
          >
            {s === "star" ? "★ Star" : s}
            <small> {MONEY_WHEEL_PAYS[s]} to 1</small>
          </button>
        ))}
      </span>
      {ctx.signUp ? (
        <SignUpToPlay watch="Guests can watch the wheel." />
      ) : (
        <div className="casino-actions">
          <ChipPicker label="Stake" values={WHEEL_STAKES} value={stake} max={Math.min(ctx.chips, Math.max(room, 0))} onPick={setStake} disabled={!ctx.betting || !open} />
          <button
            type="button"
            className="btn btn-primary btn-sm"
            disabled={!can}
            title={offTitle(ctx.betting, !open ? "Bets open again after this spin." : stake > room ? `Up to ${CASINO_RULES.wheel.maxPerRound} play chips per round.` : undefined)}
            onClick={() => ctx.send({ type: "wheel.bet", symbol, amount: stake })}
            data-testid="casino-wheel-bet"
          >
            Bet {stake} on {MONEY_WHEEL_LABELS[symbol]}
          </button>
          <button
            type="button"
            className="btn btn-sm"
            disabled={!canClear}
            title={!open ? "Bets are locked while the wheel turns." : staked === 0 ? "You have no bets on this round." : `Take back your ${chipsText(staked)}.`}
            onClick={() => ctx.send({ type: "wheel.clear" })}
            data-testid="casino-wheel-clear"
          >
            Clear bets
          </button>
        </div>
      )}
      <p className="small" data-testid="casino-wheel-mine">
        {staked > 0
          ? `Your bets: ${Object.entries(myBets)
              .map(([s, a]) => `${a} on ${MONEY_WHEEL_LABELS[s as MoneyWheelSymbol]}`)
              .join(", ")} (${staked} of ${CASINO_RULES.wheel.maxPerRound} this round)${open ? "" : ". Locked until the wheel stops."}`
          : `${ctx.signUp ? "" : "No bets this round. "}A winning number pays its number to 1, the star ${MONEY_WHEEL_PAYS.star} to 1, and your bet comes back.`}
      </p>
      {w.history.length ? (
        <p className="casino-history small">
          Last results:{" "}
          {w.history.slice(0, 10).map((s, i) => (
            <span key={i} className={`casino-pill sym-${s}`}>
              {s === "star" ? "★" : s}
            </span>
          ))}
        </p>
      ) : null}
    </div>
  );
}

// ---------------------------------------------------------------------------------------------------------------------
// Bar
// ---------------------------------------------------------------------------------------------------------------------

function BarPanel({ ctx }: { ctx: Ctx }) {
  const wait = ctx.leftMine(ctx.mine.drinkIn);
  const mine = ctx.state.drinks.find((d) => d.actorId === ctx.localActorId);
  const bartender = ctx.state.bartender;
  return (
    <div className="casino-panel" data-testid="casino-bar">
      <div className="casino-title">
        <h3>The bar</h3>
        <span className="casino-phase">{mine ? `You have a ${CASINO_DRINKS[mine.drink].name}` : "Free mocktails"}</span>
      </div>
      <p className="casino-guide" data-testid="casino-bartender-hint">
        {bartender
          ? `${bartender.label} (AI) is behind the bar: say hi in the chat, ask about the games, or order a mocktail by name. Drinks are free and alcohol-free.`
          : "Pull up to the counter and chat with whoever is around. Drinks are free and alcohol-free, just for fun."}
      </p>
      <div className="casino-actions" role="group" aria-label="Order a drink">
        {(Object.keys(CASINO_DRINKS) as CasinoDrink[]).map((d) => (
          <button
            key={d}
            type="button"
            className="btn btn-sm casino-drink"
            disabled={wait > 0}
            title={wait > 0 ? `Your next drink is ready in ${wait} s.` : undefined}
            onClick={() => ctx.send({ type: "bar.drink", drink: d })}
            data-testid={`casino-drink-${d}`}
          >
            <span className="casino-drink-dot" style={{ background: CASINO_DRINKS[d].color }} aria-hidden="true" />
            {CASINO_DRINKS[d].name}
          </button>
        ))}
      </div>
      {wait > 0 ? <p className="small casino-muted">Next drink in {wait} s.</p> : null}
    </div>
  );
}
